import React, { useState, useEffect } from 'react'
import { htmlToPlainText } from '@/utils/htmlToPlainText'
import { api, ApiError } from '@/services/api'
import {
  Card,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Tabs,
  Timeline,
  Divider,
  Statistic,
  Progress,
  Modal,
  Form,
  Input,
  Select,
  message,
  Badge,
  Table,
  Empty,
} from 'antd'
import {
  EditOutlined,
  FilePdfOutlined,
  PrinterOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  SendOutlined,
  FileOutlined,
  ClockCircleOutlined,
  CheckOutlined,
  ExclamationOutlined,
  CloseOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Proposal, ProposalStatus, PricingDetail } from '@/types'
import { generateProposalHTML, printProposalPDF } from '@/utils/proposalPdfGenerator'
import { getTemplateBody, getCoverPage, getCompanyContent, getTemplateTermsHtml } from '@/utils/proposalTemplateBody'
import { useMasterData } from '@/context/MasterDataContext'
import { authStore } from '@/context/auth'
import { leadStore } from '../Lead/leadStore'
import { proposalStore } from './proposalStore'
import { notifySuccess, notifyInfo } from '@/utils/notify'
import type { WorkflowConfig } from '@/context/MasterDataContext'
import type { ApprovalStep } from '@/types'
import { useModulePermission } from '@/hooks/useModulePermission'
import type { Lead } from '@/types'
import '../Lead/LeadList.css'

// Same {{placeholder}} set as LeadDetail.tsx's Email/WhatsApp templates (kept
// as its own small copy rather than importing that one — this fills them
// from the proposal + its lead, not a Lead record alone, so the source data
// differs even where the placeholder names match).
const interpolateProposalTemplate = (
  text: string,
  stored: { id: string; leadName?: string; referenceNumber?: string; validity: number; tariff: number; pricingDetails?: PricingDetail[] },
  lead: Lead | undefined,
  salesUser: string,
  sender?: { phone?: string; email?: string }
): string => {
  if (!text) return ''
  const rate = stored.pricingDetails?.[0]?.rate ?? stored.tariff
  return text
    .replace(/{{customerName}}/g, lead?.customerName || '')
    .replace(/{{contactPerson}}/g, lead?.customerName || '')
    .replace(/{{companyName}}/g, stored.leadName || lead?.companyName || '')
    .replace(/{{demand}}/g, lead?.sanctionedDemand ? `${lead.sanctionedDemand} ${lead.demandUnit || 'KW'}` : '')
    .replace(/{{voltage}}/g, lead?.drawlVoltage || '')
    .replace(/{{htscNumber}}/g, lead?.htscNumber || 'HTSC Number')
    .replace(/{{referenceNumber}}/g, stored.referenceNumber || stored.id)
    .replace(/{{tariff}}/g, rate ? `₹${rate}/kWh` : '')
    .replace(/{{validity}}/g, stored.validity ? `${stored.validity} days` : '')
    .replace(/{{salesUser}}/g, salesUser)
    .replace(/{{senderName}}/g, salesUser)
    .replace(/{{senderPhone}}/g, sender?.phone || '')
    .replace(/{{senderEmail}}/g, sender?.email || '')
}

// Same lookup as LeadDetail.tsx's findSenderContact — resolves the signed-in
// sales rep's phone/email from Employee Master for {{senderPhone}}/{{senderEmail}}.
const findSenderContact = (
  salesUser: string,
  employees: { name: string; phone?: string; email?: string }[]
): { phone: string; email: string } => {
  const emp = employees.find((e) => e.name === salesUser)
  return { phone: emp?.phone || '', email: emp?.email || '' }
}

/**
 * Resolve the Proposal approval chain from the Workflow Master.
 *
 * This chain is company-wide and always was — it used to be dug out of the
 * Admin role's `workflowConfigs`, falling back to any active role that had one,
 * which was a per-role field being used as a global setting. It now comes from
 * its own master (Master → Workflow Master). Returns a fresh, all-pending
 * chain — [] when the Proposal workflow isn't configured or isn't enabled.
 */
const buildProposalChain = (workflows: WorkflowConfig[]): ApprovalStep[] => {
  const source = workflows.find(
    w => w.module === 'Proposal' && w.isActive && w.status !== 'inactive'
  )
  if (!source?.steps?.length) return []
  return source.steps.map((s, i) => ({
    id: `AS${String(i + 1).padStart(2, '0')}`,
    stepNo: s.stepNo ?? i + 1,
    stepName: s.stepName,
    approverName: s.approverName,
    role: s.stepName,
    isRequired: s.isRequired,
    action: s.action,
    status: 'pending' as const,
  }))
}

const ProposalDetail: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const location = useLocation()
  // "Negotiate" from the Proposal List jumps straight here with this set, so
  // the Revision History tab is what the user sees immediately.
  const initialTab = (location.state as { openTab?: string } | null)?.openTab
  const stored = id ? proposalStore.get(id) : null
  const { proposalTemplates, employees, workflows, emailTemplates } = useMasterData()
  const { canEmail, canDownload } = useModulePermission('Proposals')
  const [emailModalVisible, setEmailModalVisible] = useState(false)
  const [emailSending, setEmailSending] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [, setRefresh] = useState(0)
  const [emailForm] = Form.useForm()

  // Hydrate from the DB on mount (after a hard refresh the in-memory store starts
  // empty) and re-render on any store change so the latest proposal + status show.
  useEffect(() => {
    const bump = () => setRefresh((n) => n + 1)
    proposalStore.refresh().then(bump)
    window.addEventListener(proposalStore.eventName, bump)
    return () => window.removeEventListener(proposalStore.eventName, bump)
  }, [])

  // Use THIS proposal's chosen template (by id, else name); fall back to default.
  const activeTemplates = proposalTemplates.filter(t => t.status === 'active')
  const defaultTemplate = activeTemplates.find(t => t.isDefault) || activeTemplates[0]
  const chosenTemplate =
    proposalTemplates.find(t => t.id === stored?.templateId) ||
    proposalTemplates.find(t => t.name === stored?.template) ||
    defaultTemplate

  // Merge the proposal's own edits over the template so the PDF body reflects
  // what was actually entered (contract period, commencement, PF charge, docs…).
  const mergedTemplate = chosenTemplate ? {
    ...chosenTemplate,
    contractPeriod:          stored?.contractTerms?.contractPeriod ?? chosenTemplate.contractPeriod,
    lockInPeriod:            stored?.contractTerms?.lockInPeriod ?? chosenTemplate.lockInPeriod,
    noticePeriod:            stored?.contractTerms?.noticePeriod ?? chosenTemplate.noticePeriod,
    commencementMonth:       stored?.contractTerms?.commencementMonth || chosenTemplate.commencementMonth,
    powerFacilitationCharge: stored?.powerFacilitationCharge ?? chosenTemplate.powerFacilitationCharge,
    gstOnFacilitationCharge: stored?.gstOnFacilitationCharge ?? chosenTemplate.gstOnFacilitationCharge,
    minimumAssuredSupply:    stored?.billingTerms?.minimumAssuredSupply || chosenTemplate.minimumAssuredSupply,
    requiredDocuments:       stored?.requiredDocuments?.length ? stored.requiredDocuments : chosenTemplate.requiredDocuments,
    signingDocuments:        stored?.signingDocuments?.length ? stored.signingDocuments : chosenTemplate.signingDocuments,
    
    // Merge proposal's custom layout & text overrides
    bodyHtml:                stored?.bodyHtml ? stored.bodyHtml : chosenTemplate.bodyHtml,
    companyContentHtml:      stored?.companyContentHtml ? stored.companyContentHtml : chosenTemplate.companyContentHtml,
    coverPageEnabled:        stored?.coverPageEnabled !== undefined && stored.coverPageEnabled !== null ? stored.coverPageEnabled : chosenTemplate.coverPageEnabled,
    coverTitle:              stored?.coverTitle ? stored.coverTitle : chosenTemplate.coverTitle,
    coverSubtitle:           stored?.coverSubtitle ? stored.coverSubtitle : chosenTemplate.coverSubtitle,
    coverDescription:        stored?.coverDescription ? stored.coverDescription : chosenTemplate.coverDescription,
    coverImage:              stored?.coverImage ? stored.coverImage : chosenTemplate.coverImage,
    termsConditionsHtml:     stored?.termsConditionsHtml ? stored.termsConditionsHtml : chosenTemplate.termsConditionsHtml,
  } : undefined
  const templateBodyHtml = getTemplateBody(mergedTemplate)
  const coverPage = getCoverPage(mergedTemplate)
  const companyContentHtml = getCompanyContent(mergedTemplate)
  const termsConditionsHtml = getTemplateTermsHtml(mergedTemplate)

  // Show only real data — no demo fallback. Not found → empty state.
  if (!stored) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Empty description="Proposal not found" />
        <Button type="primary" style={{ marginTop: 16 }} onClick={() => navigate('/proposals')}>Back to Proposals</Button>
      </div>
    )
  }

  // Approval chain: use the proposal's saved per-step progress if it exists,
  // otherwise derive a fresh (all-pending) chain from the Workflow Master.
  const matrixChain = buildProposalChain(workflows)
  const effectiveChain: ApprovalStep[] =
    stored.approvalChain && stored.approvalChain.length ? stored.approvalChain : matrixChain

  const proposal: Proposal = {
    id: stored.id,
    leadId: stored.leadId,
    revisionNumber: stored.revisionNumber,
    referenceNumber: stored.referenceNumber,
    offerDate: stored.offerDate ? new Date(stored.offerDate) : new Date(stored.createdAt),
    pricingDetails: stored.pricingDetails ?? [],
    powerFacilitationCharge: stored.powerFacilitationCharge,
    gstOnFacilitationCharge: stored.gstOnFacilitationCharge,
    deliveredTariff: stored.deliveredTariff,
    tariff: Number(stored.tariff) || 0,
    sakthiTariff: stored.sakthiTariff,
    commercialTerms: stored.commercialTerms || '',
    gstDetails: stored.gstDetails ?? { hsn: '', sac: '', placeOfSupply: '', reverseCharge: false },
    contractTerms: stored.contractTerms,
    billingTerms: stored.billingTerms,
    energySupplyAdjustments: stored.energySupplyAdjustments ?? '',
    bodyHtml: stored.bodyHtml,
    companyContentHtml: stored.companyContentHtml,
    coverPageEnabled: stored.coverPageEnabled,
    coverTitle: stored.coverTitle,
    coverSubtitle: stored.coverSubtitle,
    coverDescription: stored.coverDescription,
    coverImage: stored.coverImage,
    termsConditionsHtml: stored.termsConditionsHtml,
    validity: (stored.validity as 15 | 30) ?? 30,
    status: stored.status,
    pdfUrl: '',
    approvalChain: effectiveChain,
    changeLog: stored.changeLog ?? [],
    createdAt: new Date(stored.createdAt),
    updatedAt: new Date(stored.createdAt),
    expiryDate: new Date(stored.expiryDate),
  }
  const changeLog = stored.changeLog ?? []

  // Templates for the Send Email modal — same active-only convention as
  // LeadDetail.tsx's email quick action. Pulling the full Lead record (not
  // just the leadName this proposal denormalised) is what lets templates
  // written with {{customerName}}/{{demand}}/{{voltage}}/{{htscNumber}}
  // resolve here exactly as they do from the Lead page.
  const emailLead = leadStore.getLead(stored.leadId)
  const activeEmailTemplates = (emailTemplates || [])
    .filter((t) => t.status === 'active')
    .map((t) => ({
      key: t.id,
      label: `${t.name} (${t.category})`,
      subject: t.subject,
      body: t.bodyHtml ? htmlToPlainText(t.bodyHtml) : '',
    }))

  const statusColors: any = {
    [ProposalStatus.DRAFT]: 'default',
    [ProposalStatus.SUBMITTED]: 'processing',
    [ProposalStatus.UNDER_REVIEW]: 'warning',
    [ProposalStatus.APPROVED]: 'success',
    [ProposalStatus.REJECTED]: 'error',
    [ProposalStatus.EXPIRED]: 'default',
    [ProposalStatus.WON]: 'cyan',
    [ProposalStatus.LOST]: 'magenta',
  }

  const statusIcons: any = {
    [ProposalStatus.DRAFT]: <FileOutlined />,
    [ProposalStatus.SUBMITTED]: <ClockCircleOutlined />,
    [ProposalStatus.UNDER_REVIEW]: <ExclamationOutlined />,
    [ProposalStatus.APPROVED]: <CheckCircleOutlined />,
    [ProposalStatus.REJECTED]: <ExclamationOutlined />,
    [ProposalStatus.WON]: <CheckCircleOutlined />,
    [ProposalStatus.LOST]: <CloseCircleOutlined />,
  }

  // Customer block comes from the proposal's lead; signatory from the signed-in user.
  const lead = stored ? leadStore.getLead(stored.leadId) : undefined
  const me = employees.find(e => e.email.toLowerCase() === (authStore.getUser()?.email || '').toLowerCase())
  const signedIn = authStore.getUser()

  // ── Approval gating ──────────────────────────────────────────────────────
  // Admin can approve/reject any step; otherwise only the step's assigned
  // approver may act. The "current" step is the first one still pending.
  const isAdmin = signedIn?.role === 'admin'
  const myName = me?.name || signedIn?.name || ''
  const hasMatrix = effectiveChain.length > 0
  const currentStepIdx = effectiveChain.findIndex(s => s.status === 'pending')
  const currentStep = currentStepIdx >= 0 ? effectiveChain[currentStepIdx] : null
  const canActOnCurrentStep = !!currentStep && (isAdmin || (!!myName && myName === currentStep.approverName))

  const pdfData = {
    proposal,
    customerName: lead?.companyName || stored?.leadName || 'Customer',
    customerAddress: lead?.address || '—',
    customerCity: [lead?.city, lead?.pinCode].filter(Boolean).join(' - ') || lead?.city || '—',
    templateBodyHtml,
    coverPage,
    companyContentHtml,
    termsConditionsHtml,
    preparedBy: myName || signedIn?.email || undefined,
  }

  // Rendered server-side via Puppeteer (real headless Chromium, same engine
  // as the browser's own print-to-PDF) — html2pdf.js/html2canvas couldn't
  // reliably capture the cover page's background image. This is a genuine
  // one-click download, no print dialog / manual "Save as PDF" step.
  const handleDownloadPDF = async () => {
    setDownloadingPdf(true)
    try {
      await api.downloadProposalPdf(proposal.id, generateProposalHTML(pdfData), `Proposal_${stored?.referenceNumber || proposal.id}.pdf`)
      message.success('Proposal PDF downloaded successfully!')
    } catch (err) {
      console.error('Proposal PDF download failed:', err)
      message.error(err instanceof ApiError ? err.message : 'Failed to generate PDF download. Please try again.')
    } finally {
      setDownloadingPdf(false)
    }
  }

  const handlePrintPDF = () => {
    try {
      printProposalPDF(generateProposalHTML(pdfData))
      message.success('Print dialog opened')
    } catch {
      message.error('Failed to open for printing')
    }
  }

  // Opens with the first active template pre-applied (same convention as
  // LeadDetail.tsx's openEmailModal) — still fully editable, and a plain
  // subject/message is fine when there's no template configured yet.
  const openEmailModal = () => {
    const salesUser = signedIn?.name || signedIn?.email || 'Our Team'
    const senderContact = findSenderContact(salesUser, employees)
    const firstTpl = activeEmailTemplates[0]
    emailForm.setFieldsValue({
      template: firstTpl?.key,
      recipientEmail: emailLead?.email || '',
      subject: firstTpl
        ? interpolateProposalTemplate(firstTpl.subject, stored, emailLead, salesUser, senderContact)
        : `Proposal ${proposal.referenceNumber || proposal.id} — Shakthi Power`,
      message: firstTpl
        ? interpolateProposalTemplate(firstTpl.body, stored, emailLead, salesUser, senderContact)
        : 'Please find our commercial offer for power supply attached.',
    })
    setEmailModalVisible(true)
  }

  const handleEmailTemplateChange = (val: string) => {
    const salesUser = signedIn?.name || signedIn?.email || 'Our Team'
    const senderContact = findSenderContact(salesUser, employees)
    const tpl = activeEmailTemplates.find((t) => t.key === val)
    if (!tpl) return
    emailForm.setFieldsValue({
      subject: interpolateProposalTemplate(tpl.subject, stored, emailLead, salesUser, senderContact),
      message: interpolateProposalTemplate(tpl.body, stored, emailLead, salesUser, senderContact),
    })
  }

  const handleSendEmail = async (values: any) => {
    const subject = values.subject || `Proposal ${proposal.referenceNumber || proposal.id} — Shakthi Power`
    const body = values.message || 'Please find our commercial offer for power supply attached.'
    setEmailSending(true)
    try {
      // The backend renders this exact HTML (same as Print/Download) to a
      // real PDF via a headless Chromium — client-side html2pdf.js/
      // html2canvas couldn't reliably capture the cover page's background
      // image, so generation now happens server-side instead.
      const sentTo = await api.emailProposal(proposal.id, {
        to: values.recipientEmail,
        subject,
        body,
        htmlContent: generateProposalHTML(pdfData),
      })
      message.success(`Proposal emailed to ${sentTo.sentTo} with PDF attached`)
      setEmailModalVisible(false)
      emailForm.resetFields()
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : 'Could not send the email. Please try again.')
    } finally {
      setEmailSending(false)
    }
  }

  // ── Approve / Reject — steps through the Role-master approval chain ──────────
  // With a matrix: approve the current step (admin, or the assigned approver);
  // the proposal is APPROVED only once every REQUIRED step is approved.
  // Without a matrix: fall back to a single-click approval.
  const handleApprove = () => {
    if (!stored) return

    // No approval matrix configured → simple one-click approval.
    if (!hasMatrix) {
      proposalStore.save({ id: stored.id, status: ProposalStatus.APPROVED })
      notifySuccess('Proposal approved', `${stored.referenceNumber || stored.id} is now approved`)
      setRefresh((n) => n + 1)
      return
    }

    if (currentStepIdx === -1) return
    if (!canActOnCurrentStep) {
      message.error(`Only ${currentStep?.approverName || 'the assigned approver'} (or an admin) can approve this step.`)
      return
    }

    const updated: ApprovalStep[] = effectiveChain.map((s, i) =>
      i === currentStepIdx
        ? { ...s, status: 'approved', approvedBy: myName || 'Admin', approvalDate: new Date().toISOString() }
        : s
    )
    // Proposal passes once no REQUIRED step remains pending (optional steps don't block).
    const requiredPending = updated.some(s => s.isRequired && s.status === 'pending')
    const newStatus = requiredPending ? ProposalStatus.UNDER_REVIEW : ProposalStatus.APPROVED

    proposalStore.save({ id: stored.id, approvalChain: updated, status: newStatus })
    if (newStatus === ProposalStatus.APPROVED) {
      notifySuccess('Proposal approved', `${stored.referenceNumber || stored.id} is fully approved`)
    } else {
      const next = updated.find(s => s.status === 'pending')
      notifySuccess('Step approved', `Now awaiting ${next?.approverName || 'the next approver'} (${next?.stepName || 'next step'})`)
    }
    setRefresh((n) => n + 1)
  }

  const handleReject = () => {
    if (!stored) return

    if (!hasMatrix) {
      proposalStore.save({ id: stored.id, status: ProposalStatus.REJECTED })
      notifyInfo('Proposal rejected', `${stored.referenceNumber || stored.id} was marked as rejected`)
      setRefresh((n) => n + 1)
      return
    }

    if (currentStepIdx === -1) return
    if (!canActOnCurrentStep) {
      message.error(`Only ${currentStep?.approverName || 'the assigned approver'} (or an admin) can reject this step.`)
      return
    }

    const updated: ApprovalStep[] = effectiveChain.map((s, i) =>
      i === currentStepIdx
        ? { ...s, status: 'rejected', approvedBy: myName || 'Admin', approvalDate: new Date().toISOString() }
        : s
    )
    // A rejection at any step rejects the whole proposal.
    proposalStore.save({ id: stored.id, approvalChain: updated, status: ProposalStatus.REJECTED })
    notifyInfo('Proposal rejected', `${stored.referenceNumber || stored.id} was rejected at "${currentStep?.stepName || 'this step'}"`)
    setRefresh((n) => n + 1)
  }

  const handleMarkAsWon = () => {
    if (!stored) return
    proposalStore.save({ id: stored.id, status: ProposalStatus.WON })
    message.success('Proposal marked as Won!')
    setRefresh((n) => n + 1)
  }

  const handleMarkAsLost = () => {
    if (!stored) return
    proposalStore.save({ id: stored.id, status: ProposalStatus.LOST })
    message.success('Proposal marked as Lost!')
    setRefresh((n) => n + 1)
  }

  const daysLeft = Math.ceil(
    (new Date(proposal.expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  )

  const pricingColumns = [
    { title: 'Type of Power', dataIndex: 'supplySource', key: 'supplySource' },
    { title: 'Volume/Annum', dataIndex: 'volumePerAnnum', key: 'volumePerAnnum' },
    { title: 'Drawl Voltage', dataIndex: 'drawlVoltage', key: 'drawlVoltage' },
    {
      title: 'Sale Power Price',
      dataIndex: 'rate',
      key: 'rate',
      render: (v: number | string) => <strong style={{ color: '#001A4D' }}>₹{Number(v).toFixed(2)}/-</strong>,
    },
    { title: 'Equity Investment', dataIndex: 'equityInvestment', key: 'equityInvestment' },
    { title: 'Security Mechanism', dataIndex: 'securityMechanism', key: 'securityMechanism' },
  ]

  return (
    <div className="lead-detail-container">
      {/* Header */}
      <div className="detail-header">
        <div className="detail-title-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/proposals')}
              style={{ fontSize: '18px' }}
            />
            <div>
              <h2 style={{ margin: 0 }}>Proposal {proposal.id}</h2>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                Ref: {proposal.referenceNumber}
              </div>
            </div>
          </div>
          <div style={{ marginTop: '12px' }}>
            <Badge
              status={proposal.status === ProposalStatus.APPROVED ? 'success' : 'processing'}
              text={
                <Tag color={statusColors[proposal.status]}>
                  {statusIcons[proposal.status]} {proposal.status}
                </Tag>
              }
            />
            <Tag color="blue" style={{ marginLeft: '8px' }}>v{proposal.revisionNumber}</Tag>
          </div>
        </div>
        <Space direction="vertical" align="end">
          <Space wrap>
            <Button icon={<PrinterOutlined />} size="large" onClick={handlePrintPDF}>
              Print
            </Button>
            <Button icon={<FilePdfOutlined />} size="large" onClick={handleDownloadPDF} loading={downloadingPdf} type="default">
              Download PDF
            </Button>
            {canEmail && (
              <Button
                type="primary"
                icon={<SendOutlined />}
                size="large"
                onClick={openEmailModal}
                style={{ background: '#00A651', borderColor: '#00A651' }}
              >
                Send Email
              </Button>
            )}
            {/* Approve/Reject only make sense before a proposal reaches any
                closed/terminal state. The previous check only excluded
                Approved/Rejected, so Won/Lost/Expired proposals still showed
                these buttons — an allowlist of the actionable pre-approval
                statuses avoids that class of bug entirely. */}
            {(proposal.status === ProposalStatus.DRAFT ||
              proposal.status === ProposalStatus.SUBMITTED ||
              proposal.status === ProposalStatus.UNDER_REVIEW) && (
              (!hasMatrix || canActOnCurrentStep) ? (
                <>
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckCircleOutlined />}
                    onClick={handleApprove}
                    style={{ background: '#059669', borderColor: '#059669' }}
                  >
                    {hasMatrix && currentStep ? `Approve — ${currentStep.stepName}` : 'Approve'}
                  </Button>
                  <Button danger size="large" icon={<ExclamationOutlined />} onClick={handleReject}>
                    Reject
                  </Button>
                </>
              ) : currentStep ? (
                <Tag color="warning" style={{ fontSize: 13, padding: '6px 12px', alignSelf: 'center' }}>
                  <ClockCircleOutlined /> Awaiting {currentStep.approverName || 'approver'} — {currentStep.stepName}
                </Tag>
              ) : null
            )}
            {proposal.status === ProposalStatus.APPROVED && (
              <>
                {/* Negotiate — Admin only */}
                {isAdmin && (
                  <Button
                    icon={<EditOutlined />}
                    size="large"
                    onClick={() => navigate(`/proposals/${proposal.id}/edit`)}
                  >
                    Negotiate
                  </Button>
                )}
                {/* Mark as Won — Admin only (same rule enforced server-side
                    in crudService.js's proposals update guard). */}
                {isAdmin && (
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckOutlined />}
                    onClick={handleMarkAsWon}
                    style={{ background: '#16a34a', borderColor: '#16a34a' }}
                  >
                    Mark as Won
                  </Button>
                )}
                <Button
                  type="primary"
                  danger
                  size="large"
                  icon={<CloseOutlined />}
                  onClick={handleMarkAsLost}
                >
                  Mark as Lost
                </Button>
                <Button
                  type="primary"
                  size="large"
                  onClick={() => navigate('/agreements/new', { state: { proposalId: stored?.id } })}
                >
                  Create Agreement
                </Button>
              </>
            )}
            {proposal.status === ProposalStatus.WON && (
              <Button
                type="primary"
                size="large"
                onClick={() => navigate('/agreements/new', { state: { proposalId: stored?.id } })}
              >
                Create Agreement
              </Button>
            )}
          </Space>
          {/* Edit button — visible for all non-terminal statuses */}
          {proposal.status !== ProposalStatus.APPROVED &&
           proposal.status !== ProposalStatus.WON &&
           proposal.status !== ProposalStatus.REJECTED && (
            <Button
              icon={<EditOutlined />}
              size="large"
              onClick={() => navigate(`/proposals/${proposal.id}/edit`)}
            >
              Edit Proposal
            </Button>
          )}
        </Space>
      </div>

      <Divider />

      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card hoverable>
            <Statistic
              title="Sale Power Price"
              value={proposal.pricingDetails?.[0]?.rate || proposal.tariff}
              suffix="₹/kWh"
              valueStyle={{ color: '#DC2626' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card hoverable>
            <Statistic
              title="Contract Period"
              value={proposal.contractTerms?.contractPeriod || 3}
              suffix="Years"
              valueStyle={{ color: '#0099CC' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card hoverable>
            <Statistic
              title="Days Until Expiry"
              value={daysLeft}
              suffix="days"
              valueStyle={{ color: daysLeft < 7 ? '#ff4d4f' : '#00A651' }}
            />
            <Progress
              percent={Math.max(0, Math.min(100, ((30 - daysLeft) / 30) * 100))}
              size="small"
              showInfo={false}
              style={{ marginTop: '8px' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={[16, 16]}>
        {/* Left Sidebar */}
        <Col xs={24} lg={8}>
          <Card
            title="Proposal Details"
            style={{ marginBottom: '16px' }}
            styles={{ header: { background: '#f5f5f5', borderLeft: '3px solid #001A4D' } }}
          >
            {[
              ['Proposal ID', proposal.id],
              ['Ref Number', proposal.referenceNumber || '—'],
              ['Sale Power Price', `₹${Number(proposal.pricingDetails?.[0]?.rate || proposal.tariff).toFixed(2)}/kWh`],

              ['Created', proposal.createdAt.toLocaleDateString('en-IN')],
              ['Expiry', proposal.expiryDate.toLocaleDateString('en-IN')],
              ['Payment Terms', proposal.commercialTerms],
            ].map(([label, value]) => (
              <div key={label} className="info-row">
                <span className="info-label">{label}:</span>
                <span className="info-value">{value}</span>
              </div>
            ))}
          </Card>

          <Card
            title="Contract Terms"
            style={{ marginBottom: '16px' }}
            styles={{ header: { background: '#f5f5f5', borderLeft: '3px solid #00A651' } }}
          >
            {[
              ['Contract Period', `${proposal.contractTerms?.contractPeriod || 3} Years`],
              ['Lock-in Period', `${proposal.contractTerms?.lockInPeriod || 2} Years`],
              ['Notice Period', `${proposal.contractTerms?.noticePeriod || 6} Months`],
              ['Commencement', proposal.contractTerms?.commencementMonth || '—'],
              ['Min. Assured Supply', proposal.billingTerms?.minimumAssuredSupply || '80-85%'],
              ['Rate Revision', proposal.billingTerms?.rateRevision || '50-50%'],
            ].map(([label, value]) => (
              <div key={label} className="info-row">
                <span className="info-label">{label}:</span>
                <span className="info-value" style={{ fontWeight: 500 }}>{value}</span>
              </div>
            ))}
          </Card>

          <Card
            title="GST Details"
            styles={{ header: { background: '#f5f5f5', borderLeft: '3px solid #0099CC' } }}
          >
            {[
              ['HSN Code', proposal.gstDetails.hsn],
              ['Place of Supply', proposal.gstDetails.placeOfSupply],
            ].map(([label, value]) => (
              <div key={label} className="info-row">
                <span className="info-label">{label}:</span>
                <span className="info-value">{value}</span>
              </div>
            ))}
            <div className="info-row">
              <span className="info-label">Reverse Charge:</span>
              <Tag color={proposal.gstDetails.reverseCharge ? 'orange' : 'green'}>
                {proposal.gstDetails.reverseCharge ? 'Yes' : 'No'}
              </Tag>
            </div>
          </Card>
        </Col>

        {/* Right Tabs */}
        <Col xs={24} lg={16}>
          <Card style={{ borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,26,77,0.1)' }}>
            <Tabs
              defaultActiveKey={initialTab || 'pricing'}
              items={[
                {
                  key: 'pricing',
                  label: 'Pricing Details',
                  children: (
                    <div style={{ paddingTop: '16px' }}>
                      <Table
                        dataSource={proposal.pricingDetails}
                        columns={pricingColumns}
                        rowKey="supplySource"
                        pagination={false}
                        size="small"
                        style={{ marginBottom: '20px' }}
                        scroll={{ x: true }}
                      />

                      <div
                        style={{
                          background: '#fffbeb',
                          border: '1px solid #fde68a',
                          borderRadius: '6px',
                          padding: '12px 16px',
                          marginBottom: '12px',
                          fontSize: '13px',
                        }}
                      >
                        <div>
                          <strong>Power Facilitation Charge:</strong> ₹{proposal.powerFacilitationCharge}/unit
                          + {proposal.gstOnFacilitationCharge}% GST (exclusive of above tariff)
                        </div>
                      </div>

                      <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.6' }}>
                        * Price is inclusive of Transmission charges, system operation, scheduling
                        charges, transmission losses, cross-subsidy surcharge, Wheeling charges,
                        DSM Charges, AMR readings, Import energy charges and additional surcharges.
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'billing',
                  label: 'Billing & Payment',
                  children: (
                    <div style={{ paddingTop: '16px' }}>
                      <Row gutter={[16, 16]}>
                        {[
                          {
                            label: 'A) Billing',
                            color: '#00A651',
                            content: proposal.billingTerms?.billRaiseMethod ||
                              'Based on TANGEDCO Energy Allocation statement',
                          },
                          {
                            label: 'B) Payment Due',
                            color: '#0099CC',
                            content: `${proposal.billingTerms?.paymentDueInDays || 5}-6 working days from Invoice date`,
                          },
                          {
                            label: 'C) Payment Security',
                            color: '#6366f1',
                            content: 'Not Applicable',
                          },
                          {
                            label: 'D) Rate Revision',
                            color: '#f59e0b',
                            content: `TNEB base tariff and open access charges revision shared on ${proposal.billingTerms?.rateRevision || '50-50%'} basis`,
                          },
                          {
                            label: 'E) Min. Assured Supply',
                            color: '#ef4444',
                            content: `${proposal.billingTerms?.minimumAssuredSupply || '80 to 85%'} of contracted quantum`,
                          },
                        ].map((item) => (
                          <Col xs={24} key={item.label}>
                            <div
                              style={{
                                borderLeft: `4px solid ${item.color}`,
                                paddingLeft: '12px',
                                marginBottom: '12px',
                              }}
                            >
                              <div style={{ fontWeight: 700, fontSize: '13px', color: item.color }}>
                                {item.label}
                              </div>
                              <div style={{ fontSize: '13px', color: '#334155', marginTop: '4px' }}>
                                {item.content}
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>

                      {proposal.energySupplyAdjustments && (
                        <>
                          <Divider />
                          <div style={{ fontWeight: 700, fontSize: '13px', color: '#001A4D', marginBottom: '8px' }}>
                            H) Energy Supply & Adjustments
                          </div>
                          <ul style={{ fontSize: '13px', color: '#334155', paddingLeft: '20px', lineHeight: '1.8' }}>
                            <li>Peak hour generation shall be adjusted against peak hour consumption</li>
                            <li>Normal hour generation shall be adjusted against normal hour consumption and excess against Off-peak</li>
                            <li>After DSM implementation, as per TNERC orders</li>
                          </ul>
                        </>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'documents',
                  label: 'Documents',
                  children: (
                    <div style={{ paddingTop: '16px' }}>
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ fontWeight: 700, color: '#001A4D', marginBottom: '10px', fontSize: '13px' }}>
                          F) Required Documents
                        </div>
                        {(stored?.requiredDocuments?.length ? stored.requiredDocuments : [
                          'Last 3 Months TNEB Bill copies',
                          '0.2s Calibration Certificate',
                          'NOC from EDC (if required)',
                          'PAN Card Copy',
                          'GST Registration Certificate',
                          'Incorporation Certificate',
                          'AOA & MOA',
                          'Consent Letter & Authorisation Letter',
                        ]).map((doc) => (
                          <div
                            key={doc}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '7px 0',
                              borderBottom: '1px solid #f1f5f9',
                              fontSize: '13px',
                            }}
                          >
                            <span style={{ color: '#94a3b8' }}>•</span>
                            {doc}
                          </div>
                        ))}
                      </div>

                      <div>
                        <div style={{ fontWeight: 700, color: '#001A4D', marginBottom: '10px', fontSize: '13px' }}>
                          G) Documents to be Signed with Supplier
                        </div>
                        {(stored?.signingDocuments?.length ? stored.signingDocuments : [
                          'Power Sale Agreement (PSA) / TERM Sheet',
                          'Power Facilitation Agreement',
                        ]).map((doc) => (
                          <div
                            key={doc}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 12px',
                              background: '#fffbeb',
                              border: '1px solid #fde68a',
                              borderRadius: '6px',
                              marginBottom: '8px',
                              fontSize: '13px',
                              fontWeight: 500,
                            }}
                          >
                            <FileOutlined style={{ color: '#f59e0b' }} />
                            {doc}
                          </div>
                        ))}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'workflow',
                  label: 'Approval Workflow',
                  children: (
                    <div style={{ paddingTop: '16px' }}>
                      {proposal.approvalChain.length === 0 ? (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            <span style={{ fontSize: 13 }}>
                              No approval chain configured. Set one up in{' '}
                              <a onClick={() => navigate('/master/workflow')} style={{ color: '#2563EB', fontWeight: 600 }}>
                                Master → Workflow Master
                              </a>{' '}
                              (module: <strong>Proposal</strong>).
                            </span>
                          }
                        />
                      ) : (
                      <Timeline
                        items={proposal.approvalChain.map((step, index) => {
                          const isCurrent = step.status === 'pending' &&
                            proposal.approvalChain.findIndex(s => s.status === 'pending') === index
                          const bg = step.status === 'approved' ? '#00A651'
                            : step.status === 'rejected' ? '#DC2626'
                            : isCurrent ? '#2563EB' : '#faad14'
                          return {
                          dot: (
                            <div
                              style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                background: bg,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                              }}
                            >
                              {step.status === 'approved' ? <CheckOutlined />
                                : step.status === 'rejected' ? <ExclamationOutlined />
                                : <ClockCircleOutlined />}
                            </div>
                          ),
                          children: (
                            <div
                              style={{
                                paddingLeft: '16px',
                                paddingBottom: index === proposal.approvalChain.length - 1 ? 0 : '24px',
                              }}
                            >
                              <p style={{ margin: '0 0 4px 0', fontWeight: 600, fontSize: '14px' }}>
                                {step.stepNo ? `${step.stepNo}. ` : ''}{step.stepName || step.role}
                                {isCurrent && <Tag color="blue" style={{ marginLeft: 8, fontSize: 10 }}>Current</Tag>}
                                {step.isRequired === false && <Tag style={{ marginLeft: 8, fontSize: 10 }}>Optional</Tag>}
                              </p>
                              {step.approverName && (
                                <p style={{ margin: '2px 0', color: '#444', fontSize: '12.5px' }}>
                                  Approver: <strong>{step.approverName}</strong>
                                </p>
                              )}
                              <p style={{ margin: '4px 0', color: '#666', fontSize: '13px' }}>
                                {step.status === 'approved' ? `✓ Approved by ${step.approvedBy || '—'}`
                                  : step.status === 'rejected' ? `✕ Rejected by ${step.approvedBy || '—'}`
                                  : '⏳ Pending'}
                              </p>
                              {step.approvalDate && (
                                <p style={{ margin: '4px 0', color: '#999', fontSize: '12px' }}>
                                  {new Date(step.approvalDate).toLocaleDateString('en-IN')}{' '}
                                  {new Date(step.approvalDate).toLocaleTimeString('en-IN', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </p>
                              )}
                            </div>
                          ),
                        }})}
                      />
                      )}
                    </div>
                  ),
                },
                {
                  key: 'revisions',
                  label: `Revision History${changeLog.length ? ` (${changeLog.length})` : ''}`,
                  children: (
                    <div style={{ paddingTop: '16px' }}>
                      {changeLog.length === 0 ? (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            <span style={{ fontSize: 13 }}>
                              No changes recorded yet. Once this proposal is Approved, use{' '}
                              <strong>Negotiate</strong> to edit it — every change gets logged here.
                            </span>
                          }
                        />
                      ) : (
                        <Timeline
                          items={[...changeLog].reverse().map((entry, i) => ({
                            dot: <Tag color="blue">#{changeLog.length - i}</Tag>,
                            children: (
                              <div style={{ paddingBottom: 8 }}>
                                <p style={{ fontWeight: 600, margin: '0 0 6px 0' }}>
                                  Negotiated by {entry.changedBy}
                                </p>
                                <div style={{ background: '#F8FAFC', border: '1px solid #E4E8F0', borderRadius: 8, padding: '8px 12px', marginBottom: 4 }}>
                                  {entry.changes.map((c, j) => (
                                    <div key={j} style={{ fontSize: 12.5, padding: '3px 0', borderBottom: j === entry.changes.length - 1 ? 'none' : '1px solid #EEF1F6' }}>
                                      <span style={{ fontWeight: 600, color: '#334155' }}>{c.label}: </span>
                                      <span style={{ color: '#DC2626', textDecoration: 'line-through' }}>{c.oldValue}</span>
                                      {' → '}
                                      <span style={{ color: '#059669', fontWeight: 600 }}>{c.newValue}</span>
                                    </div>
                                  ))}
                                </div>
                                <p style={{ margin: '4px 0 0', color: '#999', fontSize: '12px' }}>
                                  {new Date(entry.date).toLocaleDateString('en-IN')}{' '}
                                  {new Date(entry.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            ),
                          }))}
                        />
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Email Modal */}
      <Modal
        title="Send Proposal via Email"
        open={emailModalVisible}
        onCancel={() => setEmailModalVisible(false)}
        footer={null}
        width={640}
      >
        <Form form={emailForm} layout="vertical" onFinish={handleSendEmail}>
          <Form.Item name="template" label="Template">
            <Select
              placeholder={activeEmailTemplates.length ? 'Choose a template' : 'No active email templates — add one in Master → Email Templates'}
              allowClear
              disabled={!activeEmailTemplates.length}
              onChange={handleEmailTemplateChange}
              options={activeEmailTemplates.map((t) => ({ label: t.label, value: t.key }))}
            />
          </Form.Item>
          <Form.Item
            name="recipientEmail"
            label="Recipient Email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter valid email' },
            ]}
          >
            <Input placeholder="customer@example.com" size="large" />
          </Form.Item>
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Subject is required' }]}
          >
            <Input placeholder="Enter email subject" size="large" />
          </Form.Item>
          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: 'Message is required' }]}
          >
            <Input.TextArea
              placeholder="Please find attached our commercial offer for power supply..."
              rows={6}
            />
          </Form.Item>
          <Space style={{ marginTop: '8px' }}>
            <Button onClick={() => setEmailModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={emailSending} style={{ background: '#00A651', borderColor: '#00A651' }}>
              Send Email
            </Button>
          </Space>
        </Form>
      </Modal>
    </div>
  )
}

export default ProposalDetail
